import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, RefreshCw } from "lucide-react";
import { ChipInput, Field, INPUT_CLS } from "./ChipInput";
import { IndustryFocusPicker, MAX_FOCUS, splitFocus } from "./IndustryFocusPicker";
import { ENTITY_TYPES } from "./onboardingModel";

const Card = ({ title, children, testId }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-gray-500">{title}</p>
    {children}
  </div>
);

export const StepConfirm = ({ data, patch, patchSpec, busy, onBack, onNext, onReanalyze }) => {
  const intel = data.analysis?.sellerIntelligence || {};
  const spec = data.spec || {};
  const setIntel = (p) => patch((prev) => ({ analysis: { ...prev.analysis, sellerIntelligence: { ...prev.analysis?.sellerIntelligence, ...p } } }));
  const pages = data.analysis?.websiteAnalysis?.pageCount;
  const industries = spec.industries;
  const tooMany = (industries || []).length > MAX_FOCUS && !(spec.parkedIndustries || []).length;
  useEffect(() => { if (tooMany) patchSpec(splitFocus(industries)); }, [tooMany, industries, patchSpec]); // drafts from before the focus picker: park everything past the first three

  return (
    <div className="space-y-5" data-testid="onboarding-step-confirm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl text-white font-light">Here&apos;s what I learned about <span className="text-ism-teal">{data.companyName || "your company"}</span></h2>
          <p className="text-gray-400 mt-1 text-sm">{pages ? `Read ${pages} pages of your site. ` : ""}Fix anything that&apos;s off — this becomes your targeting.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onReanalyze} disabled={busy} className="text-gray-400 hover:text-white flex-shrink-0" data-testid="onboarding-reanalyze-btn"><RefreshCw className="h-4 w-4 mr-1" /> Re-analyze</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Company" testId="confirm-company-card">
          <input value={data.companyName} onChange={(e) => patch({ companyName: e.target.value })} className={INPUT_CLS} placeholder="Company name" data-testid="confirm-company-input" />
          <textarea value={intel.valueProposition || ""} onChange={(e) => setIntel({ valueProposition: e.target.value })} rows={3} className={`${INPUT_CLS} resize-none text-sm`} placeholder="What you do, in one sentence" data-testid="confirm-valueprop-input" />
        </Card>

        <Card title="What you sell" testId="confirm-products-card">
          <ChipInput values={intel.productsList || []} onChange={(v) => { setIntel({ productsList: v }); patch({ products: v.join(", ") }); }} placeholder="Add a product or service" testId="confirm-products" />
          <Field label="Your buyers are…">
            <div className="flex flex-wrap gap-2">
              {ENTITY_TYPES.map((t) => {
                const on = (spec.entityTypes || []).includes(t);
                return (
                  <button key={t} onClick={() => patchSpec({ entityTypes: on ? (spec.entityTypes || []).filter((x) => x !== t) : [...(spec.entityTypes || []), t] })}
                    className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-colors ${on ? "bg-ism-teal/20 border-ism-teal/40 text-ism-teal" : "border-white/10 text-gray-400 hover:border-white/20"}`}
                    data-testid={`entity-type-${t}`}>
                    {t === "company" ? "companies" : t.endsWith("y") ? `${t.slice(0, -1)}ies` : `${t}s`}
                  </button>
                );
              })}
            </div>
          </Field>
        </Card>

        <Card title="Industries / segments that buy — pick 1–3 to hunt first" testId="confirm-industries-card">
          <IndustryFocusPicker industries={spec.industries || []} parkedIndustries={spec.parkedIndustries || []} onChange={(v) => patchSpec(v)} testId="confirm-industries" />
        </Card>

        <Card title="Who signs the deal (job titles)" testId="confirm-personas-card">
          <ChipInput values={spec.personas || []} onChange={(v) => patchSpec({ personas: v })} placeholder="e.g. CFO, Managing Partner" testId="confirm-personas" />
        </Card>
      </div>

      <Field label="Who you sell to, in one sentence" hint="This is the summary the agent reasons from.">
        <textarea value={spec.summary || ""} onChange={(e) => patchSpec({ summary: e.target.value })} rows={2} className={`${INPUT_CLS} resize-none text-sm`} data-testid="confirm-summary-input" />
      </Field>

      <div className="flex justify-between pt-2">
        <Button variant="ghost" onClick={onBack} className="text-gray-400 hover:text-white" data-testid="onboarding-back-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        <Button onClick={onNext} disabled={busy || !(spec.industries || []).length} className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium rounded-xl px-6" data-testid="onboarding-confirm-next">Looks right <ArrowRight className="h-4 w-4 ml-2" /></Button>
      </div>
    </div>
  );
};
